import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import { OpenAPI } from '../api/core/OpenAPI';
import { request as __request } from '../api/core/request';
import '../../sass/pages/iconsult.scss';

interface IConsultSession {
  id?: number;
  email: string;
  p2pFlag: boolean;
  environment: string;
  apiUserName: string;
  apiPassword: string;
  apiEncryptKey: string;
  deviceId: string;
  displayName: string;
  userId: string;
  poh: boolean;
  sessionId: string;
  userType: string;
  appointmentTime: string;
  csn: string;
  updated: number;
  sessionName: string;
}

interface InitializeCallResponse {
  call_token: string;
  message: string;
}

const IConsultTesting: React.FC = () => {
  const [environment, setEnvironment] = useState('env3');
  const [apiUserName, setApiUserName] = useState('');
  const [apiPassword, setApiPassword] = useState('');
  const [apiEncryptKey, setApiEncryptKey] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [userId, setUserId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [additionalParams, setAdditionalParams] = useState('');
  const [privateMode, setPrivateMode] = useState(false);
  const [tsInSeconds, setTsInSeconds] = useState(false);
  
  const [generatedLink, setGeneratedLink] = useState('');
  const [philipsLink, setPhilipsLink] = useState('');
  const [callToken, setCallToken] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState<'success' | 'error' | 'info'>('info');
  
  const [sessions, setSessions] = useState<string[]>([]);
  const [sessionName, setSessionName] = useState('');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const environments = [
    { value: 'stage', label: 'Staging' },
    { value: 'prod', label: 'Production' },
    { value: 'env1', label: 'ENV1' },
    { value: 'env2', label: 'ENV2' },
    { value: 'env3', label: 'ENV3' },
    { value: 'env3proxy', label: 'ENV3 API Proxy' },
    { value: 'env3philips', label: 'ENV3 API Philips' },
    { value: 'env4', label: 'ENV4' },
    { value: 'env5', label: 'ENV5' },
    { value: 'env5noruby', label: 'ENV5-NoRuby' },
    { value: 'env6', label: 'ENV6' },
    { value: 'env7', label: 'ENV7' },
    { value: 'env9', label: 'ENV9' },
    { value: 'env10', label: 'ENV10' },
    { value: 'kpnonprod', label: 'KP Non-Prod' },
    { value: 'kprod', label: 'KP Prod' },
    { value: 'japan', label: 'Japan' },
    { value: 'japanproxy', label: 'Japan API Proxy' },
    { value: 'japanphilips', label: 'Japan API Philips' }

  ];

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      console.log('Loading iConsult sessions...');
      const response: any = await __request(OpenAPI, {
        method: "GET",
        url: "/api/apitest/iconsult/sessions-list/",
        query: { 
          p2pFlag: false,
          email: 'user@example.com'  // Add email parameter
        }
      });
      console.log('Sessions response:', response);
      setSessions(response.map((session: any) => session.sessionName));
      console.log('Mapped sessions:', response.map((session: any) => session.sessionName));
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const getEnvironmentUrl = (env: string, type: 'epic' | 'philips' = 'epic') => {
    const urls: { [key: string]: string } = {
      'stage': 'https://uhehastag.univago.com/',
      'prod': 'https://healthcare.univago.com/',
      'env1': 'https://env1-healthcare.caregility.net/',
      'env2': 'https://env2-healthcare.caregility.net/',
      'env3': 'https://env3-healthcare.caregility.net/',
      'env3proxy': 'https://env3-connectapigweproxy.caregility.net/',
      'env3philips': 'https://env3-connectapigwphilips.caregility.net/',
      'env4': 'https://env4-healthcare.caregility.net/',
      'env5': 'https://env5-healthcare.caregility.net/',
      'env6': 'https://env6-healthcare.caregility.net/',
      'env7': 'https://env7-healthcare.caregility.net/',
      'env9': 'https://env9-healthcare.caregility.net/',
      'env10': 'https://env10-healthcare.caregility.net/',
      'kpnonprod': 'https://uheconnectapigwepic-dev.kp.org/',
      'kprod': 'https://uheconnectapigwepic.kp.org/',
      'japan': 'https://healthcare.caregility.care/',
      'japanproxy': 'https://connectapigweproxy.caregility.care/',
      'japanphilips': 'https://connectapigwphilips.caregility.care/'
    };

    if (env === 'env5noruby') {
      return type === 'epic' 
        ? 'https://env5-connectapigwepic.caregility.net/'
        : 'https://env5-connectapigweproxy.caregility.net/';
    }

    return urls[env] || 'https://en3te1vyaxim1.x.pipedream.net/';
  };

  const encryptText = async (plainText: string, secret: string): Promise<string> => {
    try {
//      const response = await fetch('http://localhost:8000/encrypt/', {
        const response = await fetch('https://platformapi-testing-be.caregility.net/encrypt/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken') || '',
        },
        body: JSON.stringify({
          text: plainText,
          key: secret
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Encryption failed');
      }

      const data = await response.json();
      return data.encrypted_data;
    } catch (error) {
      console.error('Encryption error:', error);
      throw error;
    }
  };

  const getCookie = (name: string): string => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || '';
    return '';
  };

  const generateLink = async () => {
    if (!apiUserName || !apiEncryptKey || !deviceId || !displayName) {
      setStatusMessage('Error - at least one of the required parameters is missing!');
      setStatusType('error');
      return;
    }

    try {
      let url = getEnvironmentUrl(environment);
      
      if (environment === 'test') {
        url = 'https://test.example.com/';
      }

      url += 'api/epic_call?api_username=' + apiUserName.trim();
      if (privateMode) {
        url += '&private=true';
      }
      url += '&data=';

      let clearText = '';
      if (environment === 'test') {
        clearText = 'timestamp=1234567890';
      } else {
        const timestamp = tsInSeconds 
          ? Math.floor(Date.now() / 1000)
          : Date.now();
        clearText = 'timestamp=' + timestamp;
      }

      if (deviceId.includes('@')) {
        clearText += '&email=' + deviceId.trim();
      } else {
        clearText += '&device_id=' + deviceId.trim();
      }

      clearText += '&user=' + displayName;

      if (userId) {
        clearText += '&userid=' + userId;
      }

      if (sessionId) {
        clearText += '&session_id=' + sessionId;
      }

      if (departmentId) {
        clearText += '&dep=' + departmentId;
      }

      if (additionalParams) {
        clearText += '&' + additionalParams;
      }

      const encryptedData = await encryptText(clearText, apiEncryptKey.trim());
      const finalLink = url + encryptedData;

      if (environment === 'test') {
        setGeneratedLink(finalLink + '?clear=' + clearText + '?key=' + apiEncryptKey.trim());
      } else {
        setGeneratedLink(finalLink);
      }

      setStatusMessage('Link generated successfully! (one-time use only!)');
      setStatusType('success');
    } catch (error) {
      setStatusMessage('Error generating link: ' + error);
      setStatusType('error');
    }
  };

  const philipsInitializeCall = async () => {
    if (!apiUserName || !apiPassword || !deviceId || !displayName || !userId) {
      setStatusMessage('Error - at least one of the required parameters is missing!');
      setStatusType('error');
      return;
    }

    setLoading(true);
    try {
      const response: any = await __request(OpenAPI, {
        method: "POST",
        url: "/api/apitest/iconsult/philips/initialize-call/",
        body: {
          environment,
          apiUserName: apiUserName.trim(),
          apiPassword: apiPassword.trim(),
          deviceId: deviceId.trim(),
          displayName,
          userId,
          additionalParams,
          privateMode
        }
      });

      if (response.philipsLink) {
        setPhilipsLink(response.philipsLink);
        setCallToken(response.callToken || '');
        setStatusMessage(response.statusMessage || 'Call initialized successfully');
        setStatusType('success');
      } else {
        setStatusMessage(response.statusMessage || 'Failed to initialize call');
        setStatusType('error');
      }
    } catch (error: any) {
      setStatusMessage('Error initializing call: ' + (error.message || error));
      setStatusType('error');
    } finally {
      setLoading(false);
    }
  };

  const philipsEndCall = async () => {
    if (!callToken || !userId) {
      setStatusMessage('Error - Call token or User ID is missing!');
      setStatusType('error');
      return;
    }

    setLoading(true);
    try {
      const response: any = await __request(OpenAPI, {
        method: "POST",
        url: "/api/apitest/iconsult/philips/end-call/",
        body: {
          environment,
          apiUserName: apiUserName.trim(),
          apiPassword: apiPassword.trim(),
          userId,
          callToken
        }
      });

      setStatusMessage(response.statusMessage || 'Call ended successfully');
      setStatusType('success');
      setPhilipsLink('');
      setCallToken('');
    } catch (error: any) {
      setStatusMessage('Error ending call: ' + (error.message || error));
      setStatusType('error');
    } finally {
      setLoading(false);
    }
  };

  const saveSession = async () => {
    if (!sessionName || !apiUserName) {
      setStatusMessage('Error - Session Name and API User Name are required.');
      setStatusType('error');
      return;
    }

    try {
      const session: IConsultSession = {
        email: 'user@example.com', // This should come from authentication
        p2pFlag: false,
        environment,
        apiUserName: apiUserName?.trim() || '',
        apiPassword: apiPassword?.trim() || '',
        apiEncryptKey: apiEncryptKey?.trim() || '',
        deviceId: deviceId?.trim() || '',
        displayName: displayName || '',
        userId: userId || '',
        poh: privateMode,
        sessionId: sessionId || '',
        userType: '',
        appointmentTime: '',
        csn: departmentId?.trim() || '',
        updated: Date.now(),
        sessionName
      };

      await __request(OpenAPI, {
        method: "POST",
        url: "/api/apitest/iconsult/sessions-save/",
        body: session
      });

      setStatusMessage('Session saved successfully');
      setStatusType('success');
      await loadSessions();
    } catch (error: any) {
      setStatusMessage('Error saving session: ' + (error.message || error));
      setStatusType('error');
    }
  };

  const deleteSession = async () => {
    if (!sessionName) {
      setStatusMessage('Error - Session Name is required.');
      setStatusType('error');
      return;
    }

    try {
      await __request(OpenAPI, {
        method: "DELETE",
        url: "/api/apitest/iconsult/sessions-delete/",
        query: {
          email: 'user@example.com',
          p2pFlag: false,
          sessionName
        }
      });

      setStatusMessage('Session deleted successfully');
      setStatusType('success');
      setSessionName('');
      await loadSessions();
    } catch (error: any) {
      setStatusMessage('Error deleting session: ' + (error.message || error));
      setStatusType('error');
    }
  };

  const loadSession = async (sessionName: string) => {
    if (!sessionName || sessionName === '---') {
      setSessionName('');
      return;
    }

    try {
      const response: any = await __request(OpenAPI, {
        method: "GET",
        url: `/api/apitest/iconsult/session-detail/`,
        query: {
          email: 'user@example.com',
          p2pFlag: false,
          sessionName: sessionName
        }
      });

      if (response) {
        setEnvironment(response.environment);
        setApiUserName(response.apiUserName);
        setApiPassword(response.apiPassword);
        setApiEncryptKey(response.apiEncryptKey);
        setDeviceId(response.deviceId);
        setDisplayName(response.displayName);
        setUserId(response.userId);
        setSessionId(response.sessionId);
        setDepartmentId(response.csn);
        setPrivateMode(response.poh);
        setSessionName(sessionName);
      }
    } catch (error: any) {
      setStatusMessage('Error loading session: ' + (error.message || error));
      setStatusType('error');
    }
  };





  const openGeneratedLink = () => {
    if (generatedLink) {
      window.open(generatedLink, '_blank');
    }
  };

  const openPhilipsLink = () => {
    if (philipsLink) {
      window.open(philipsLink, '_blank');
    }
  };

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  return (
    <Container fluid className="iconsult-container">
      <Row>
        <Col>
          <h1>iConsult 2.0 Call</h1>
          <p>
            Choose environment and provide required parameters (displayed in <strong>bold</strong>). 
            Default: Staging - "Caregility Test" customer.
            <br />
            Use buttons below to create links to share, or make calls. (Sessions can be saved for future use.)
          </p>

          <Row className="main-content-flex">
            {/* Left Sidebar */}
            <div className={`sidebar left ${sidebarOpen ? "open" : "closed"}`}>
              <Button variant="outline-primary" size="sm" onClick={toggleSidebar} className="toggle-btn">
                {sidebarOpen ? "←" : "→"}
              </Button>

              {sidebarOpen && (
                <>
                  <h4>Saved Sessions</h4>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Saved Sessions:</strong></Form.Label>
                    <Form.Select 
                      value={sessionName} 
                      onChange={(e) => loadSession(e.target.value)}
                    >
                      <option value="---">---</option>
                      {sessions.map((session, index) => (
                        <option key={index} value={session}>{session}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <h4>Session Management</h4>
                  <Form.Group className="mb-3">
                    <Form.Label>Session Name:</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={sessionName} 
                      onChange={(e) => setSessionName(e.target.value)}
                      placeholder="Enter session name"
                    />
                  </Form.Group>
                  <div className="d-grid gap-2">
                    <Button variant="success" size="sm" onClick={saveSession}>
                      Save Session
                    </Button>
                    <Button variant="danger" size="sm" onClick={deleteSession}>
                      Delete Session
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Center Content */}
            <div className="center-content">
              <h4>Actions</h4>
              
              <Row>
                <Col md={6}>
                  <h6><strong>Epic:</strong></h6>
                  <div className="d-grid gap-2">
                    <Button variant="primary" onClick={generateLink}>
                      Generate Link
                    </Button>
                    <Button variant="primary" onClick={openGeneratedLink} disabled={!generatedLink}>
                      Encrypted API Call
                    </Button>
                    <Button variant="secondary" disabled>
                      SMART on FHIR (Coming soon)
                    </Button>
                  </div>
                </Col>
                <Col md={6}>
                  <h6><strong>Philips:</strong></h6>
                  <div className="d-grid gap-2">
                    <Button 
                      variant="primary" 
                      onClick={philipsInitializeCall}
                      disabled={loading}
                    >
                      {loading ? 'Initializing...' : 'Make Call'}
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={openPhilipsLink}
                      disabled={!philipsLink}
                    >
                      Open Call
                    </Button>
                    <Button 
                      variant="danger" 
                      onClick={philipsEndCall}
                      disabled={loading || !callToken}
                    >
                      {loading ? 'Ending...' : 'End Call'}
                    </Button>
                  </div>
                </Col>
              </Row>

              {statusMessage && (
                <Alert variant={statusType} className="mb-4">
                  <strong>Status:</strong> {statusMessage}
                </Alert>
              )}

              {generatedLink && (
                <Card className="mb-4">
                  <Card.Header>
                    <h5>Generated Link</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Control 
                      as="textarea" 
                      rows={3} 
                      value={generatedLink} 
                      readOnly 
                    />
                  </Card.Body>
                </Card>
              )}

              <h4>Configuration</h4>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Environment:</strong></Form.Label>
                    <Form.Select value={environment} onChange={(e) => setEnvironment(e.target.value)}>
                      {environments.map((env) => (
                        <option key={env.value} value={env.value}>{env.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>API User Name:</strong> [From admin portal]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={apiUserName} 
                      onChange={(e) => setApiUserName(e.target.value)}
                      placeholder="API User Name"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Password:</strong> [From admin portal - for Philips]</Form.Label>
                    <Form.Control 
                      type="password" 
                      value={apiPassword} 
                      onChange={(e) => setApiPassword(e.target.value)}
                      placeholder="API Password"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Encrypt Key:</strong> [From admin portal - for Epic]</Form.Label>
                    <Form.Control 
                      type="password" 
                      value={apiEncryptKey} 
                      onChange={(e) => setApiEncryptKey(e.target.value)}
                      placeholder="API Encrypt Key"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Device ID:</strong> [for APS, e.g. 1234] <strong>or Email:</strong> [for APD, e.g. room77@xyz.com]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={deviceId} 
                      onChange={(e) => setDeviceId(e.target.value)}
                      placeholder="Device ID or Email"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Display Name:</strong> [Name of the provider, e.g. Dr. John Doe]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={displayName} 
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Display Name"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>User ID:</strong> [e.g. 123456 - for Epic, this is optional]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={userId} 
                      onChange={(e) => setUserId(e.target.value)}
                      placeholder="User ID"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Session ID: [Unique session identifier, e.g. 122333444455555]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={sessionId} 
                      onChange={(e) => setSessionId(e.target.value)}
                      placeholder="Session ID"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Department ID: [e.g. XYZ-123]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={departmentId} 
                      onChange={(e) => setDepartmentId(e.target.value)}
                      placeholder="Department ID"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Additional Parameters: [Separated by "&"; e.g. virtualbg=true&endcallbtn=true]</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={additionalParams} 
                      onChange={(e) => setAdditionalParams(e.target.value)}
                      placeholder="Additional Parameters"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Check 
                    type="checkbox" 
                    label="Patient on Hold [If checked - Camera/sound off; Screen blank]"
                    checked={privateMode}
                    onChange={(e) => setPrivateMode(e.target.checked)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Check 
                    type="checkbox" 
                    label="Timestamp in Seconds [If checked - timestamp will be in seconds, otherwise in milliseconds]"
                    checked={tsInSeconds}
                    onChange={(e) => setTsInSeconds(e.target.checked)}
                  />
                </Col>
              </Row>
            </div>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default IConsultTesting;
